/**
 * @file
 * Keep in sync with widget/src/app/utils/chat.tsx
 * until we figure out code-sharing solution!
 */
import React from 'react';
import clsx from 'clsx';
import { User } from 'sb-lib/types';
import { convertToHtml } from './widget-copy';

// https://stackoverflow.com/a/61157860
const URL_REGEX = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/;
const BR_REGEX = /<br\s?\/?>/g;
const SHORT_MENTION_REGEX = /@\((\d+)\)/;
const MARKDOWN_LINK_REGEX = /\[([^\]]+)\]\(([^)]+)\)/;
const BOLD_REGEX = /\*\*(.*?)(\*\*|$)/;

export const parseMessage = (text: string, members: User[], plainText: boolean) => {
  const normalizedText = text.replace(/\\n/g, '\n');

  return normalizedText.split(/(\[.*?\]\(.*?\))|\s+/).map((part, idx) => {
    if (!part) return;

    const markdownLinkMatch = part.match(MARKDOWN_LINK_REGEX);
    const boldMatch = part.match(BOLD_REGEX);

    if (markdownLinkMatch) {
      const [, linkText, linkUrl] = markdownLinkMatch;

      const fullUrl = linkUrl.startsWith('https://') ? linkUrl : `https://${linkUrl}`;

      if (plainText) return <React.Fragment key={idx}>{linkText}</React.Fragment>;

      return (
        <React.Fragment key={idx}>
          <a href={fullUrl} target="_blank" rel="noreferrer">
            {linkText}
          </a>{' '}
        </React.Fragment>
      );
    }

    if (URL_REGEX.test(part) && !plainText) {
      return (
        <React.Fragment key={idx}>
          <a href={part} target="_blank" rel="noreferrer">
            {part}
          </a>{' '}
        </React.Fragment>
      );
    }

    if (boldMatch) {
      const [, boldText] = boldMatch;

      if (plainText) {
        return `${boldText} `;
      }

      return (
        <React.Fragment key={idx}>
          <b>{boldText}</b>{' '}
        </React.Fragment>
      );
    }

    if (SHORT_MENTION_REGEX.test(part)) {
      const [_, userId] = part.match(SHORT_MENTION_REGEX) || [];
      const user = members.find(m => m.id === parseInt(userId, 10));
      if (!user) return ' ';
      return (
        <React.Fragment key={idx}>
          <span className={clsx(!plainText && 'SessionChatMessage-mention')}>
            @{user.displayName}
          </span>{' '}
        </React.Fragment>
      );
    }

    return <React.Fragment key={idx}>{part} </React.Fragment>;
  });
};

export const renderMessage = (text: string, members: User[]) => {
  const htmlText = convertToHtml(text);
  if (!htmlText) return <></>;
  const pieces = htmlText.split(BR_REGEX).map((item, idx) => (
    <React.Fragment key={idx}>
      {idx !== 0 && <br />}
      {parseMessage(item.trim(), members, false)}
    </React.Fragment>
  ));
  return <>{pieces}</>;
};
